import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { groupBy, sortBy, uniq } from 'lodash';

import type { SearchableSelectOption } from '@/components/SearchableSelect';
import { BOOL_LABELS } from '@/constants/common';
import type PRODUCTS from '@/constants/products.constants';
import { PRODUCT_AUTO_SOC_SUB_TYPES } from '@/constants/products.constants';
import useHandleError from '@/hooks/useHandleError';
import { axiosClient } from '@/services/BaseApi.service';

import {
  PRODUCT_EXPIRATION_WARNING_PERIOD,
  PRODUCT_TYPES_TO_PULL
} from './Organizations.constants';
import {
  type Organization,
  type OrganizationProductStatus,
  type OrganizationProductsDTO,
  OrganizationType,
  type ProductDTO
} from './Organizations.types';

export const productIsExpired = (product: ProductDTO) => {
  const now = dayjs();
  return dayjs(product.end_date).isBefore(now, 'day');
};

export const productIsExpiringSoon = (product: ProductDTO) => {
  const now = dayjs();
  const end = dayjs(product.end_date);
  return (
    end.isAfter(now, 'day') &&
    now.add(PRODUCT_EXPIRATION_WARNING_PERIOD, 'day').isAfter(end, 'day')
  );
};

export const getProductSubTypeDisplay = (subType?: string | null): string => {
  if (!subType) return '';

  const mapped =
    PRODUCT_AUTO_SOC_SUB_TYPES[
      subType as keyof typeof PRODUCT_AUTO_SOC_SUB_TYPES
    ];

  return mapped || subType;
};

export const mapOrganizationsDTO = (
  organizations: OrganizationProductsDTO[]
): Organization[] => {
  const now = dayjs();
  const productTypes = PRODUCT_TYPES_TO_PULL;

  const orgs = sortBy(organizations, 'name').map((organization) => {
    const orgProducts =
      organization.products?.filter((product) =>
        productTypes.includes((product.product_type as PRODUCTS) || '')
      ) || [];
    const isExpiringSoon = orgProducts.some(productIsExpiringSoon);
    const isExpired = orgProducts.every(productIsExpired);
    const productType =
      orgProducts.map((product) => product.product_type || '') || [];

    const productSubType = (
      orgProducts.map((product) =>
        getProductSubTypeDisplay(product.sub_type)
      ) || []
    ).filter(Boolean);

    let status: OrganizationProductStatus = 'Active';
    if (isExpired || (orgProducts || []).length === 0) {
      status = 'Expired';
    } else if (isExpiringSoon) {
      status = 'Expiring Soon';
    }

    let activeProducts = 0;
    orgProducts?.forEach((product) => {
      const start = dayjs(product.start_date);
      const end = dayjs(product.end_date);
      if (start.isSameOrBefore(now, 'day') && end.isSameOrAfter(now, 'day')) {
        activeProducts += 1;
      }
    });

    return {
      ...organization,
      products: sortBy(orgProducts, ['type', 'code']),
      product_status: status,
      product_type: uniq(productType),
      product_sub_type: uniq(productSubType),
      active_products: activeProducts,
      total_users: organization.total_users || 0,
      ai_enabled: organization.organization_settings?.ai_enable_submission
        ? BOOL_LABELS.ENABLED
        : BOOL_LABELS.DISABLED,
      type: organization.is_ir
        ? OrganizationType.IR
        : OrganizationType.SALESFORCE
    };
  });
  return orgs;
};

export const getProductTypeFilterOptions = (
  organizations: Organization[]
): SearchableSelectOption[] => {
  const options = organizations.flatMap((org) => org.product_type);

  const groupedOptions = groupBy(options);
  return Object.keys(groupedOptions).map((key) => ({
    label: key,
    value: key,
    count: groupedOptions[key].length,
    id: key
  }));
};

export const getProductSubTypeFilterOptions = (
  organizations: Organization[]
): SearchableSelectOption[] => {
  const options = organizations.flatMap((org) => org.product_sub_type);

  const groupedOptions = groupBy(options);
  return Object.keys(groupedOptions).map((key) => ({
    label: key,
    value: key,
    count: groupedOptions[key].length,
    id: key
  }));
};

/* Mutation to update organization, currently only used on admin/general page to update org name */
export const useMutationOrganizations = (
  refetch: () => void,
  organizationId: number
) => {
  const handleError = useHandleError();
  return useMutation({
    mutationFn: (newInfo: Organization) => {
      return axiosClient.put(`organizations/${organizationId}`, newInfo);
    },
    onSuccess: () => {
      refetch();
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to update organization.');
    }
  });
};
